// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import api, { fetchUserProfileApi } from '../services/api';

const AuthContext = createContext(null);

// 20 minutes of IDLE inactivity before session logout
const SESSION_TIMEOUT_DURATION = 20 * 60 * 1000;

export const AuthProvider = ({ children }) => {
    const getValidToken = (keyName) => {
        const val = localStorage.getItem(keyName);
        if (!val || val === 'undefined' || val === 'null' || typeof val !== 'string' || val.trim() === '') {
            return null;
        }
        return val;
    };

    const [user, setUser] = useState(null);
    const [token, setToken] = useState(getValidToken('authToken'));
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        console.log("AuthContext: Logging out user due to inactivity or manual trigger.");
        localStorage.removeItem('authToken');
        localStorage.removeItem('loginTimestamp');
        localStorage.removeItem('lastActivityTimestamp');
        setToken(null);
        setUser(null);
    }, []);

    // Throttle updating activity timestamp to max once every 5 seconds
    const lastUpdateRef = useRef(0);
    const updateActivity = useCallback(() => {
        const now = Date.now();
        if (now - lastUpdateRef.current > 5000) {
            lastUpdateRef.current = now;
            localStorage.setItem('lastActivityTimestamp', now.toString());
        }
    }, []);

    // Active user activity listeners (mousemove, keydown, click, scroll, touchstart)
    useEffect(() => {
        if (!token) return;

        // Set initial activity timestamp if missing
        if (!localStorage.getItem('lastActivityTimestamp')) {
            localStorage.setItem('lastActivityTimestamp', Date.now().toString());
        }

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        const handleUserActivity = () => updateActivity();

        events.forEach(event => {
            window.addEventListener(event, handleUserActivity, { passive: true });
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, handleUserActivity);
            });
        };
    }, [token, updateActivity]);

    useEffect(() => {
        let sessionTimeoutId = null;

        const checkSessionTimeout = () => {
            const lastActivity = localStorage.getItem('lastActivityTimestamp') || localStorage.getItem('loginTimestamp');
            if (token && lastActivity) {
                const now = Date.now();
                const idleDuration = now - parseInt(lastActivity, 10);
                if (idleDuration > SESSION_TIMEOUT_DURATION) {
                    console.log("Session expired due to 20 minutes of inactivity.");
                    alert("Your session has expired due to 20 minutes of inactivity. Please log in again.");
                    logout();
                }
            } else if (sessionTimeoutId) {
                clearInterval(sessionTimeoutId);
                sessionTimeoutId = null;
            }
        };

        const loadUserAndStartSession = async () => {
            setLoading(true);

            const currentTokenFromStorage = getValidToken('authToken');
            const lastActivity = localStorage.getItem('lastActivityTimestamp') || localStorage.getItem('loginTimestamp');

            if (currentTokenFromStorage) {
                if (token !== currentTokenFromStorage) {
                    setToken(currentTokenFromStorage);
                }

                if (lastActivity && (Date.now() - parseInt(lastActivity, 10) > SESSION_TIMEOUT_DURATION)) {
                    console.log("AuthContext: Inactivity timeout reached on load.");
                    logout();
                    return;
                }

                try {
                    const { data } = await fetchUserProfileApi();
                    setUser(data);
                    if (sessionTimeoutId) clearInterval(sessionTimeoutId);
                    sessionTimeoutId = setInterval(checkSessionTimeout, 30 * 1000); // Check idle timeout every 30s
                } catch (error) {
                    console.error("AuthContext: Failed to load user, logging out.", error);
                    logout();
                }
            } else {
                setUser(null);
                if (token !== null) setToken(null);
                if (sessionTimeoutId) {
                    clearInterval(sessionTimeoutId);
                    sessionTimeoutId = null;
                }
            }
            setLoading(false);
        };

        loadUserAndStartSession();

        return () => {
            if (sessionTimeoutId) {
                clearInterval(sessionTimeoutId);
            }
        };
    }, [token, logout]);

    const login = (newToken, userDataFromServer) => {
        if (!newToken || newToken === 'undefined' || newToken === 'null' || typeof newToken !== 'string' || newToken.trim() === '') {
            console.error("AuthContext: Invalid token passed to login function!", newToken);
            return;
        }
        const now = Date.now();
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('loginTimestamp', now.toString());
        localStorage.setItem('lastActivityTimestamp', now.toString());
        setUser(userDataFromServer);
        setToken(newToken);
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);